import { FACE_SHAPES } from '../data/shapeContent';
import type {
  FaceAnalysis,
  FaceShapeId,
  GeometryMetrics,
  LandmarkPoint,
  ManualMeasurements,
  MatchCharacter,
  MeasurementSegment,
  Point2D,
  QualityNote,
  ShapeMatch,
} from '../types';

export const FACE_OVAL_INDICES = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378,
  400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54,
  103, 67, 109,
] as const;

const PROFILE_KEYS: Array<keyof GeometryMetrics> = [
  'lengthToWidth',
  'foreheadRatio',
  'cheekRatio',
  'jawRatio',
  'lowerJawRatio',
  'foreheadToJaw',
  'cheekProminence',
  'jawAngle',
  'chinTaper',
];

export const SHAPE_PROFILES: Record<FaceShapeId, GeometryMetrics> = {
  oval: {
    lengthToWidth: 1.39,
    foreheadRatio: 0.91,
    cheekRatio: 1,
    jawRatio: 0.83,
    lowerJawRatio: 0.64,
    foreheadToJaw: 1.1,
    cheekProminence: 1.1,
    jawAngle: 128,
    chinTaper: 0.77,
  },
  round: {
    lengthToWidth: 1.13,
    foreheadRatio: 0.94,
    cheekRatio: 1,
    jawRatio: 0.91,
    lowerJawRatio: 0.79,
    foreheadToJaw: 1.03,
    cheekProminence: 1.05,
    jawAngle: 145,
    chinTaper: 0.87,
  },
  square: {
    lengthToWidth: 1.18,
    foreheadRatio: 0.96,
    cheekRatio: 1,
    jawRatio: 0.95,
    lowerJawRatio: 0.85,
    foreheadToJaw: 1.01,
    cheekProminence: 1.03,
    jawAngle: 106,
    chinTaper: 0.9,
  },
  oblong: {
    lengthToWidth: 1.61,
    foreheadRatio: 0.94,
    cheekRatio: 1,
    jawRatio: 0.89,
    lowerJawRatio: 0.72,
    foreheadToJaw: 1.06,
    cheekProminence: 1.05,
    jawAngle: 122,
    chinTaper: 0.81,
  },
  heart: {
    lengthToWidth: 1.34,
    foreheadRatio: 0.98,
    cheekRatio: 0.98,
    jawRatio: 0.74,
    lowerJawRatio: 0.53,
    foreheadToJaw: 1.32,
    cheekProminence: 1.03,
    jawAngle: 120,
    chinTaper: 0.72,
  },
  diamond: {
    lengthToWidth: 1.4,
    foreheadRatio: 0.79,
    cheekRatio: 1,
    jawRatio: 0.72,
    lowerJawRatio: 0.52,
    foreheadToJaw: 1.1,
    cheekProminence: 1.32,
    jawAngle: 118,
    chinTaper: 0.72,
  },
  triangle: {
    lengthToWidth: 1.28,
    foreheadRatio: 0.77,
    cheekRatio: 0.91,
    jawRatio: 1,
    lowerJawRatio: 0.86,
    foreheadToJaw: 0.77,
    cheekProminence: 0.94,
    jawAngle: 108,
    chinTaper: 0.86,
  },
};

const PROFILE_TOLERANCE: GeometryMetrics = {
  lengthToWidth: 0.18,
  foreheadRatio: 0.1,
  cheekRatio: 0.08,
  jawRatio: 0.1,
  lowerJawRatio: 0.11,
  foreheadToJaw: 0.16,
  cheekProminence: 0.14,
  jawAngle: 20,
  chinTaper: 0.1,
};

const PROFILE_WEIGHT: GeometryMetrics = {
  lengthToWidth: 1.8,
  foreheadRatio: 0.8,
  cheekRatio: 0.75,
  jawRatio: 1,
  lowerJawRatio: 0.8,
  foreheadToJaw: 1.55,
  cheekProminence: 1.25,
  jawAngle: 1.2,
  chinTaper: 0.9,
};

interface RotationTransform {
  angle: number;
  rotate: (point: Point2D) => Point2D;
  unrotate: (point: Point2D) => Point2D;
}

interface ScanlineResult {
  left: Point2D;
  right: Point2D;
  width: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function pointAt(landmarks: LandmarkPoint[], index: number): Point2D {
  const point = landmarks[index];
  if (!point) {
    throw new Error(`Required face landmark ${index} is missing.`);
  }
  return { x: point.x, y: point.y };
}

function distance(a: Point2D, b: Point2D): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function angleAt(vertex: Point2D, first: Point2D, second: Point2D): number {
  const ax = first.x - vertex.x;
  const ay = first.y - vertex.y;
  const bx = second.x - vertex.x;
  const by = second.y - vertex.y;
  const magnitude = Math.hypot(ax, ay) * Math.hypot(bx, by);
  if (magnitude === 0) return 120;
  const cosine = clamp((ax * bx + ay * by) / magnitude, -1, 1);
  return (Math.acos(cosine) * 180) / Math.PI;
}

function makeRotation(leftEye: Point2D, rightEye: Point2D): RotationTransform {
  const center = {
    x: (leftEye.x + rightEye.x) / 2,
    y: (leftEye.y + rightEye.y) / 2,
  };
  const angle = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x);
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);

  return {
    angle,
    rotate(point) {
      const dx = point.x - center.x;
      const dy = point.y - center.y;
      return {
        x: center.x + dx * cosine + dy * sine,
        y: center.y - dx * sine + dy * cosine,
      };
    },
    unrotate(point) {
      const dx = point.x - center.x;
      const dy = point.y - center.y;
      return {
        x: center.x + dx * cosine - dy * sine,
        y: center.y + dx * sine + dy * cosine,
      };
    },
  };
}

export function scanlineAcrossPolygon(polygon: Point2D[], y: number): ScanlineResult | null {
  const intersections: number[] = [];

  for (let index = 0; index < polygon.length; index += 1) {
    const current = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    if (!current || !next) continue;

    const crosses =
      (current.y <= y && next.y > y) || (next.y <= y && current.y > y);
    if (!crosses) continue;

    const ratio = (y - current.y) / (next.y - current.y);
    intersections.push(current.x + ratio * (next.x - current.x));
  }

  if (intersections.length < 2) return null;
  intersections.sort((a, b) => a - b);
  const leftX = intersections[0];
  const rightX = intersections.at(-1);
  if (leftX === undefined || rightX === undefined || rightX <= leftX) return null;

  return {
    left: { x: leftX, y },
    right: { x: rightX, y },
    width: rightX - leftX,
  };
}

function segmentAtLevel(
  polygon: Point2D[],
  minY: number,
  height: number,
  level: number,
): ScanlineResult {
  const segment = scanlineAcrossPolygon(polygon, minY + height * level);
  if (!segment) {
    throw new Error(`Unable to measure the face outline at level ${level}.`);
  }
  return segment;
}

function widestSegment(
  polygon: Point2D[],
  minY: number,
  height: number,
  startLevel: number,
  endLevel: number,
): ScanlineResult {
  let best: ScanlineResult | null = null;
  for (let level = startLevel; level <= endLevel; level += 0.025) {
    const candidate = scanlineAcrossPolygon(polygon, minY + height * level);
    if (candidate && (!best || candidate.width > best.width)) {
      best = candidate;
    }
  }
  if (!best) throw new Error('Unable to find a stable face width.');
  return best;
}

function weightedDistance(metrics: GeometryMetrics, profile: GeometryMetrics): number {
  let weightedSquares = 0;
  let totalWeight = 0;

  for (const key of PROFILE_KEYS) {
    const normalizedDelta = (metrics[key] - profile[key]) / PROFILE_TOLERANCE[key];
    const weight = PROFILE_WEIGHT[key];
    weightedSquares += normalizedDelta * normalizedDelta * weight;
    totalWeight += weight;
  }

  return Math.sqrt(weightedSquares / totalWeight);
}

export function scoreFaceShapeMetrics(metrics: GeometryMetrics): ShapeMatch[] {
  return (Object.keys(SHAPE_PROFILES) as FaceShapeId[])
    .map((id) => {
      const profileDistance = weightedDistance(metrics, SHAPE_PROFILES[id]);
      const strength = Math.round(clamp(96 - profileDistance * 20, 32, 96));
      return { id, strength, distance: profileDistance };
    })
    .sort((a, b) => b.strength - a.strength || a.distance - b.distance);
}

function classifyCharacter(matches: ShapeMatch[]): MatchCharacter {
  const first = matches[0];
  const second = matches[1];
  if (!first || !second) return 'close';
  const gap = first.strength - second.strength;
  if (gap <= 6) return 'blended';
  if (gap >= 12 && first.strength >= 72) return 'strong';
  return 'close';
}

function widthLeader(metrics: GeometryMetrics): string {
  const widths = [
    { name: 'forehead', value: metrics.foreheadRatio },
    { name: 'cheekbone area', value: metrics.cheekRatio },
    { name: 'jaw', value: metrics.jawRatio },
  ];
  widths.sort((a, b) => b.value - a.value);
  const first = widths[0];
  const second = widths[1];
  if (!first || !second) return 'facial widths are relatively balanced';
  if (first.value - second.value < 0.035) return 'the main facial widths are closely balanced';
  return `the ${first.name} is the widest measured region`;
}

function describeJaw(metrics: GeometryMetrics): string {
  if (metrics.jawAngle < 114) return 'the jaw corners read as relatively angular';
  if (metrics.chinTaper < 0.72) return 'the lower face narrows into a pronounced taper';
  if (metrics.jawAngle > 137) return 'the jaw transition reads as soft and curved';
  return 'the jaw has a moderate, gradual taper';
}

function buildExplanation(primary: FaceShapeId, metrics: GeometryMetrics): string {
  const ratio = metrics.lengthToWidth.toFixed(2);
  const content = FACE_SHAPES[primary];
  return `${content.label} is the closest geometry match: your visible face length is about ${ratio}× its widest point, ${widthLeader(metrics)}, and ${describeJaw(metrics)}.`;
}

function buildReasons(metrics: GeometryMetrics): string[] {
  const reasons = [
    `Length-to-width ratio: ${metrics.lengthToWidth.toFixed(2)}:1.`,
    `Forehead-to-jaw ratio: ${metrics.foreheadToJaw.toFixed(2)}:1.`,
    `Cheek prominence: ${metrics.cheekProminence.toFixed(2)} relative to the wider of forehead or jaw.`,
  ];

  if (metrics.jawAngle < 114) {
    reasons.push('Jaw corners are more angular than curved in the measured outline.');
  } else if (metrics.jawAngle > 137) {
    reasons.push('Jaw corners transition gradually, which reads as a softer outline.');
  } else {
    reasons.push('Jaw corners sit between strongly angular and fully rounded.');
  }

  return reasons;
}

function buildAnalysis(
  source: FaceAnalysis['source'],
  metrics: GeometryMetrics,
  oval: Point2D[] = [],
  segments: MeasurementSegment[] = [],
  quality: QualityNote[] = [],
): FaceAnalysis {
  const matches = scoreFaceShapeMetrics(metrics);
  const primary = matches[0];
  if (!primary) throw new Error('Face shape scoring did not return a result.');

  return {
    source,
    metrics,
    matches,
    primary,
    character: classifyCharacter(matches),
    explanation: buildExplanation(primary.id, metrics),
    reasons: buildReasons(metrics),
    quality,
    oval,
    segments,
  };
}

function qualityNotes(
  landmarks: LandmarkPoint[],
  oval: Point2D[],
  rotation: RotationTransform,
): QualityNote[] {
  const notes: QualityNote[] = [];
  const xs = oval.map((point) => point.x);
  const ys = oval.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const coverage = (maxX - minX) * (maxY - minY);
  const rollDegrees = Math.abs((rotation.angle * 180) / Math.PI);

  if (coverage < 0.12) {
    notes.push({
      code: 'small-face',
      level: 'warning',
      message: 'Your face occupies a small part of the photo. A closer crop may improve the contour measurement.',
    });
  }

  if (rollDegrees > 11) {
    notes.push({
      code: 'head-roll',
      level: 'warning',
      message: 'Your head is noticeably tilted. The tool corrected for roll, but a level photo can give a steadier result.',
    });
  }

  const leftCheek = pointAt(landmarks, 234);
  const rightCheek = pointAt(landmarks, 454);
  const nose = pointAt(landmarks, 1);
  const cheekWidth = distance(leftCheek, rightCheek);
  const noseOffset = Math.abs(nose.x - (leftCheek.x + rightCheek.x) / 2) / cheekWidth;
  if (noseOffset > 0.075) {
    notes.push({
      code: 'side-angle',
      level: 'warning',
      message: 'The face appears slightly turned. A straight-on photo improves left/right width comparisons.',
    });
  }

  if (minX < 0.015 || maxX > 0.985 || minY < 0.015 || maxY > 0.985) {
    notes.push({
      code: 'edge-crop',
      level: 'warning',
      message: 'Part of the visible face outline is close to the photo edge. Leave a little space around the head.',
    });
  }

  return notes;
}

export function analyzeFaceLandmarks(landmarks: LandmarkPoint[]): FaceAnalysis {
  if (landmarks.length < 455) {
    throw new Error('The detected face mesh is incomplete. Try a clearer, front-facing photo.');
  }

  const leftEye = pointAt(landmarks, 33);
  const rightEye = pointAt(landmarks, 263);
  const rotation = makeRotation(leftEye, rightEye);
  const oval = FACE_OVAL_INDICES.map((index) => pointAt(landmarks, index));
  const rotatedOval = oval.map(rotation.rotate);
  const ys = rotatedOval.map((point) => point.y);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const height = maxY - minY;
  if (height <= 0) throw new Error('The detected face outline has invalid geometry.');

  const overall = widestSegment(rotatedOval, minY, height, 0.18, 0.78);
  const forehead = segmentAtLevel(rotatedOval, minY, height, 0.24);
  const cheekbones = widestSegment(rotatedOval, minY, height, 0.36, 0.58);
  const jaw = segmentAtLevel(rotatedOval, minY, height, 0.72);
  const lowerJaw = segmentAtLevel(rotatedOval, minY, height, 0.84);
  const maxWidth = overall.width;

  const leftJawAngle = angleAt(
    rotation.rotate(pointAt(landmarks, 172)),
    rotation.rotate(pointAt(landmarks, 234)),
    rotation.rotate(pointAt(landmarks, 152)),
  );
  const rightJawAngle = angleAt(
    rotation.rotate(pointAt(landmarks, 397)),
    rotation.rotate(pointAt(landmarks, 454)),
    rotation.rotate(pointAt(landmarks, 152)),
  );
  const jawAngle = clamp((leftJawAngle + rightJawAngle) / 2, 90, 165);

  const metrics: GeometryMetrics = {
    lengthToWidth: height / maxWidth,
    foreheadRatio: forehead.width / maxWidth,
    cheekRatio: cheekbones.width / maxWidth,
    jawRatio: jaw.width / maxWidth,
    lowerJawRatio: lowerJaw.width / maxWidth,
    foreheadToJaw: forehead.width / jaw.width,
    cheekProminence: cheekbones.width / Math.max(forehead.width, jaw.width),
    jawAngle,
    chinTaper: lowerJaw.width / jaw.width,
  };

  const top = rotatedOval.reduce((best, point) => (point.y < best.y ? point : best));
  const bottom = rotatedOval.reduce((best, point) => (point.y > best.y ? point : best));
  const segments: MeasurementSegment[] = [
    {
      id: 'length',
      label: 'Face length',
      start: rotation.unrotate(top),
      end: rotation.unrotate(bottom),
    },
    {
      id: 'forehead',
      label: 'Forehead',
      start: rotation.unrotate(forehead.left),
      end: rotation.unrotate(forehead.right),
    },
    {
      id: 'cheekbones',
      label: 'Cheekbones',
      start: rotation.unrotate(cheekbones.left),
      end: rotation.unrotate(cheekbones.right),
    },
    {
      id: 'jaw',
      label: 'Jaw',
      start: rotation.unrotate(jaw.left),
      end: rotation.unrotate(jaw.right),
    },
  ];

  return buildAnalysis('photo', metrics, oval, segments, qualityNotes(landmarks, oval, rotation));
}

export function validateManualMeasurements(measurements: Partial<ManualMeasurements>): string[] {
  const values = [
    measurements.faceLength,
    measurements.foreheadWidth,
    measurements.cheekboneWidth,
    measurements.jawWidth,
  ];

  if (values.some((value) => typeof value !== 'number' || !Number.isFinite(value) || value <= 0)) {
    return ['Enter a positive number for all four measurements.'];
  }

  const [faceLength, foreheadWidth, cheekboneWidth, jawWidth] = values as [
    number,
    number,
    number,
    number,
  ];
  const maxWidth = Math.max(foreheadWidth, cheekboneWidth, jawWidth);
  const minWidth = Math.min(foreheadWidth, cheekboneWidth, jawWidth);
  const ratio = faceLength / maxWidth;
  const errors: string[] = [];

  if (ratio < 0.82 || ratio > 2.15) {
    errors.push('Check the face-length measurement; it is far outside the usual relationship to face width.');
  }
  if (minWidth / maxWidth < 0.48) {
    errors.push('One width is much smaller than the others. Recheck that every width is measured straight across.');
  }
  if (!measurements.jawCharacter) {
    errors.push('Choose the jaw description that looks closest in the mirror.');
  }

  return errors;
}

export function analyzeManualMeasurements(measurements: ManualMeasurements): FaceAnalysis {
  const errors = validateManualMeasurements(measurements);
  if (errors.length > 0) throw new Error(errors[0]);

  const maxWidth = Math.max(
    measurements.foreheadWidth,
    measurements.cheekboneWidth,
    measurements.jawWidth,
  );
  const jawFactor =
    measurements.jawCharacter === 'soft'
      ? { lower: 0.86, angle: 145 }
      : measurements.jawCharacter === 'angular'
        ? { lower: 0.9, angle: 106 }
        : { lower: 0.7, angle: 120 };
  const lowerJawWidth = measurements.jawWidth * jawFactor.lower;

  const metrics: GeometryMetrics = {
    lengthToWidth: measurements.faceLength / maxWidth,
    foreheadRatio: measurements.foreheadWidth / maxWidth,
    cheekRatio: measurements.cheekboneWidth / maxWidth,
    jawRatio: measurements.jawWidth / maxWidth,
    lowerJawRatio: lowerJawWidth / maxWidth,
    foreheadToJaw: measurements.foreheadWidth / measurements.jawWidth,
    cheekProminence:
      measurements.cheekboneWidth /
      Math.max(measurements.foreheadWidth, measurements.jawWidth),
    jawAngle: jawFactor.angle,
    chinTaper: lowerJawWidth / measurements.jawWidth,
  };

  return buildAnalysis('manual', metrics, [], [], [
    {
      code: 'manual-estimate',
      level: 'info',
      message: 'Manual results depend on where the tape was placed and on the selected jaw description. Repeating the measurements is a useful second check.',
    },
  ]);
}
